import React, { useCallback, useEffect, useMemo } from "react";
import { init, useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import { useEvents } from "./context/EventsContext";
import { getEPGFirstFocusKey, safeSetFocus } from "./utils/shared/focus";
import { EVENT_STATUS, EVENT_TITLES, SECTION_TYPES } from "./constants/event";
import { capitalizeFirstLetter } from "./utils/shared/string";
import EPGSectionRow from "./EPGSectionRow";
import EPGFilteredEventsRow from "./EPGFilteredSectionRow";
import sortEvents from './utils/shared/sortEvents';
import "./epg.scss";

init({
    debug: false,
    visualDebug: false,
});

const getRowsData = (liveEvents, upcomingEvents, scheduledEvents, sportsData, leaguesData) => {
    return [
        {
            render: !!liveEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "live",
            title: EVENT_TITLES[EVENT_STATUS.LIVE],
            items: liveEvents,
            virtualizationParams: {}, // We use the default params for the event rows
        },
        {
            render: !!sportsData.length,
            rowType: SECTION_TYPES.SPORTS,
            rowId: "sports",
            title: EVENT_TITLES[SECTION_TYPES.SPORTS],
            items: sportsData,
            virtualizationParams: {
                cardWidth: 168,
                cardGap: 24,
                amountOfCardsInWindow: 15,
            },
        },
        {
            render: !!leaguesData.length,
            rowType: SECTION_TYPES.LEAGUES,
            rowId: "leagues",
            title: EVENT_TITLES[SECTION_TYPES.LEAGUES],
            items: leaguesData,
            virtualizationParams: {
                cardWidth: 168,
                cardGap: 24,
                amountOfCardsInWindow: 15,
            },
        },
        {
            render: !!upcomingEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "upcoming",
            title: EVENT_TITLES[EVENT_STATUS.COMING],
            items: upcomingEvents,
            virtualizationParams: {},
        },
        {
            render: !!scheduledEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "scheduled",
            title: EVENT_TITLES[EVENT_STATUS.SCHEDULED],
            items: scheduledEvents,
            virtualizationParams: {},
        },
    ];
};

const EPGContent = ({ sportsData, leaguesData, onEnterPress, deeplinkEvent, shouldApplyComcastStyles }) => {
    const { liveEvents, upcomingEvents, scheduledEvents, showFiltered, selectedSport, handleFilterSelect, filteredEvents: getFilteredEvents, activeLiveEvent } = useEvents();

    const sortedLiveEvents = useMemo(() => {
        return sortEvents(liveEvents, activeLiveEvent);
    }, [liveEvents]);

    const { ref, focusKey } = useFocusable({
        focusKey: "epg-content-container",
    });

    const rowsData = useMemo(() => getRowsData(sortedLiveEvents, upcomingEvents, scheduledEvents, sportsData, leaguesData), [sortedLiveEvents, upcomingEvents, scheduledEvents, sportsData, leaguesData]);

    // Focus management
    useEffect(() => {
        if (!showFiltered && !deeplinkEvent) {
            const focusKey = getEPGFirstFocusKey(rowsData);
            // When returning to main view, focus on first available element
            setTimeout(() => {
                safeSetFocus(focusKey);
            }, 50);
        }
    }, [showFiltered]);

    const handleEnterPress = useCallback(
        (focusProps, rowType) => {
            const { focusKey } = focusProps;

            switch (rowType) {
                case SECTION_TYPES.SPORTS:
                    const sport = sportsData.find((s) => String(s.id) === String(focusKey));
                    if (sport) handleFilterSelect(sport, { type: "sport" });
                    break;
                case SECTION_TYPES.LEAGUES:
                    const league = leaguesData.find((l) => String(l.id) === String(focusKey));
                    if (league) handleFilterSelect(league, { type: "league" });
                    break;
                default:
                    onEnterPress(focusProps);
            }
        },
        [onEnterPress, sportsData, leaguesData, handleFilterSelect]
    );

    const renderMainContent = () => {
        return (
            <div>
                {rowsData
                    .filter((row) => row.render)
                    .map((row, index) => (
                        <EPGSectionRow key={`${row.rowType}-${index}`} rowType={row.rowType} rowId={row.rowId} title={row.title} items={row.items} virtualizationParams={row.virtualizationParams} onEnterPress={(focusProps) => handleEnterPress(focusProps, row.rowType)} />
                    ))}
            </div>
        );
    };

    // Render the filtered events
    const renderFilteredContent = () => {
        const rawTitle = selectedSport?.name || selectedSport?.alias || "Filtered Events";
        const title = `${capitalizeFirstLetter(rawTitle)} Events`;
        const filteredEvents = getFilteredEvents();

        return <EPGFilteredEventsRow events={filteredEvents} title={title} onEnterPress={onEnterPress} shouldApplyComcastStyles={shouldApplyComcastStyles} />;
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="containerWrapper">
                <div className="scrollingRows">{showFiltered ? renderFilteredContent() : renderMainContent()}</div>
            </div>
        </FocusContext.Provider>
    );
};
export default EPGContent;
